// Card ⋮ Remove Loyalty Discount (M4-F9; rows M4.28, DC-108 / DC-110 / DC-153), prototype loyRmModal.
// CRM Head only, on a Loyalty booking whose existing VJ customer is an applicant (discount applied or offered),
// until Final Review approval. Move the lead to Referral or Direct · no VJOP, a reason, and whether the
// customer sees a note. The server works out Now vs After removal (POST /crm2/loyalty/remove, preview).
import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import Close from "../../Icons/Close";
import Button from "../../../Subcomponents/Button";
import { crm2RemoveLoyalty } from "../../../Services/Crm2List";
import { dismissLoader, presentLoader } from "../../../Services/loader";
import { errorTost, successTost } from "../../../Services/Toast";
import { sdrDone, unitLine } from "../ui/cardParts";
import settingsCss from "../Settings/Crm2Settings.module.css";

const inr = (n) => `₹ ${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const btnSec = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", border: "1px solid #D0D5DD", fontSize: "1.4rem", lineHeight: "2rem" };
const btnDanger = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#D92D20", border: "1px solid #D92D20", fontSize: "1.4rem", lineHeight: "2rem" };
const lbl = { display: "block", fontSize: "1.4rem", fontWeight: 500, color: "#344054", marginBottom: ".6rem" };
const hint = { fontSize: "1.3rem", color: "#667085" };

function Option({ name, value, checked, onChange, title, children }) {
  return (
    <label style={{ display: "flex", gap: ".8rem", alignItems: "flex-start", marginBottom: "1.2rem", cursor: "pointer" }}>
      <input type="radio" name={name} value={value} checked={checked} onChange={() => onChange(value)} style={{ marginTop: ".3rem" }} />
      <span>
        <b style={{ color: "#101828", fontSize: "1.4rem" }}>{title}</b>
        <br />
        <span style={hint}>{children}</span>
      </span>
    </label>
  );
}

export default function RemoveLoyaltyModal({ card, onClose, onDone }) {
  const apId = card.allotmentPaymentId;
  const [moveTo, setMoveTo] = useState("REFERRAL");
  const [reason, setReason] = useState("");
  const [show, setShow] = useState(null);
  const [view, setView] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setView(null);
    crm2RemoveLoyalty(apId, { moveTo, preview: true })
      .then((r) => {
        setView(r.data);
        setShow((s) => (s === null ? (r.data?.showDefault !== false ? "show" : "silent") : s));
      })
      .catch((e) => {
        errorTost(e?.response?.data?.message || "Could not open this booking. Please try again.");
        onClose();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apId, moveTo]);

  const applied = !!card.discounts?.loyaltyApplied;
  const ex = card.discounts?.existingCustomer || "the existing VJ customer";
  const pts = view?.points ? `${Number(view.points).toLocaleString("en-IN")} VJOP points` : "VJOP referral points";

  const submit = async () => {
    if (reason.trim().length < 5) {
      setErr("Please enter the reason");
      return;
    }
    presentLoader();
    try {
      const res = await crm2RemoveLoyalty(apId, { moveTo, reason: reason.trim(), showToCustomer: show === "show" });
      console.log("[crm2][RemoveLoyalty] removed", { allotmentPaymentId: apId, moveTo, show, increase: res?.data?.increase });
      successTost(`Loyalty discount removed · now ${moveTo === "DIRECT" ? "Direct (no VJOP)" : "Referral"}.`);
      onDone();
    } catch (e) {
      console.error("[crm2][RemoveLoyalty] failed", { allotmentPaymentId: apId, status: e?.response?.status, message: e?.response?.data?.message });
      errorTost(e?.response?.data?.message || "Could not remove the loyalty discount. Please try again.");
    } finally {
      dismissLoader();
    }
  };

  const cell = { textAlign: "right" };
  return (
    <Modal open onClose={onClose} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={settingsCss.modalWrapper} style={{ width: "64rem", maxWidth: "calc(100% - 3.2rem)" }}>
        <div className={settingsCss.modalHeader}>
          <span>Remove loyalty discount</span>
          <span className={settingsCss.modalClose} onClick={onClose} tabIndex={0} role="button" aria-label="Close">
            <Close width="1.2rem" height="1.2rem" />
          </span>
        </div>
        <div className={settingsCss.modalContent} style={{ maxHeight: "70vh", overflow: "auto" }}>
          <p style={{ fontSize: "1.5rem", fontWeight: 600, color: "#101828", margin: "0 0 .4rem" }}>
            {unitLine(card)} · {card.leadName}
          </p>
          <p style={{ ...hint, marginBottom: "1.6rem" }}>
            Now: <b style={{ color: "#101828" }}>Loyalty</b> · existing VJ customer {ex} is an applicant · {applied ? "discount applied (approved by the customer)" : "discount offered, not yet approved by the customer"}
          </p>
          <label style={lbl}>
            Move this lead to <sup style={{ color: "red" }}>*</sup>
          </label>
          <Option name="lrk" value="REFERRAL" checked={moveTo === "REFERRAL"} onChange={setMoveTo} title="Referral">
            {ex} stays the referrer and earns {pts} in VJOP after the agreement is registered. No loyalty discount.
          </Option>
          <Option name="lrk" value="DIRECT" checked={moveTo === "DIRECT"} onChange={setMoveTo} title="Direct · no VJOP">
            No loyalty discount and no referral points. The lead is no longer linked to VJOP for this booking.
          </Option>
          <label style={{ ...lbl, marginTop: ".8rem" }} htmlFor="lrr">
            Reason <sup style={{ color: "red" }}>*</sup>
          </label>
          <textarea
            id="lrr"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setErr("");
            }}
            placeholder="Why is the loyalty discount removed?"
            style={{ width: "100%", minHeight: "8rem", fontSize: "1.5rem", padding: "1rem", border: "1px solid #D0D5DD", borderRadius: ".8rem", boxSizing: "border-box" }}
          />
          {err && <p style={{ color: "#D92D20", fontSize: "1.3rem", margin: ".4rem 0 0" }}>{err}</p>}
          <label style={{ ...lbl, marginTop: "1.6rem" }}>
            Show this to the customer? <sup style={{ color: "red" }}>*</sup>
          </label>
          <Option name="lrs" value="show" checked={show === "show"} onChange={setShow} title="Yes · show a note on the Customer Portal">
            The customer sees "VJ has removed your loyalty discount" with the new booking type, VJOP points, this reason and the new total, and gets it by email and WhatsApp.
          </Option>
          <Option name="lrs" value="silent" checked={show === "silent"} onChange={setShow} title="No · change it directly">
            No note and no reason on the Customer Portal or in messages. The new price and booking type simply show, and the customer still confirms the new review draft by OTP (price changed). The reason stays in Timeline And Notes for VJ only.
          </Option>
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: ".4rem 2rem", fontSize: "1.4rem", margin: "1.6rem 0", background: "#F9FAFB", borderRadius: ".8rem", padding: "1.2rem 1.6rem" }}>
            <b>Item</b>
            <b style={cell}>Now</b>
            <b style={cell}>After removal</b>
            <span>Agreement value</span>
            <span style={cell}>{view ? inr(view.now.av) : "…"}</span>
            <span style={cell}>{view ? inr(view.after.av) : "…"}</span>
            <span>GST + stamp duty &amp; charges</span>
            <span style={cell}>{view ? inr(view.now.gst + view.now.sdr) : "…"}</span>
            <span style={cell}>{view ? inr(view.after.gst + view.after.sdr) : "…"}</span>
            <span>
              <b>Total unit cost</b>
            </span>
            <b style={cell}>{view ? inr(view.now.total) : "…"}</b>
            <b style={{ ...cell, color: "#B42318" }}>{view ? inr(view.after.total) : "…"}</b>
          </div>
          <div style={{ background: "#FFFAEB", border: "1px solid #FEDF89", color: "#B54708", borderRadius: ".8rem", padding: "1rem 1.4rem", fontSize: "1.35rem" }}>
            What happens: the price goes back to the actual unit price; the Sales App and VJOP lead type change; a new review draft revision goes to the customer for OTP; {sdrDone(card) ? "the customer pays the stamp duty & charges difference into the virtual account; " : ""}the CRM is told; the customer is told only if you choose to show it; saved in Timeline And Notes. Allowed until Final Review is approved.
          </div>
        </div>
        <div className={settingsCss.modalFooter}>
          <Button style={btnSec} btnName="Cancel" clicked={onClose} />
          <Button style={btnDanger} btnName="Remove loyalty discount" clicked={submit} disabled={!view} />
        </div>
      </Box>
    </Modal>
  );
}
